import React, { useEffect, useState, ChangeEvent } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Breadcrumb, BreadcrumbItem, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MdOutlineAdd, MdOutlineVisibility, MdEdit } from 'react-icons/md';
import { FaTrash, FaPlay } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fetchDataApi, deleteDataSourceApi, DataSourceListItem } from 'src/services/dataSourceServices';

// Dummy data for demo purposes
const dummyDataSources: DataSourceListItem[] = [
  {
    id: "DS001",
    dataSourceName: "AWS EC2 Instances",
    assetType: "Service / Server",
    integrationMethod: "API Pull",
    updateFrequency: "Daily",
    clientId: "AWS_CLIENT_001",
    description: "Pull EC2 instance data from AWS API to track cloud server assets",
    status: "Active",
    lastRun: "2024-01-15 10:30:00",
    assetsCount: 25,
    createdAt: "2024-01-01T10:00:00Z"
  },
  {
    id: "DS002",
    dataSourceName: "Oracle Database Servers",
    assetType: "Database",
    integrationMethod: "Database",
    updateFrequency: "Weekly",
    clientId: "ORACLE_CLIENT_002",
    description: "Direct database connection to Oracle CMDB to extract database server information",
    status: "Active",
    lastRun: "2024-01-14 15:45:00",
    assetsCount: 12,
    createdAt: "2024-01-02T14:30:00Z"
  },
  {
    id: "DS003",
    dataSourceName: "Network Devices Inventory",
    assetType: "Network",
    integrationMethod: "File",
    updateFrequency: "Monthly",
    clientId: "NETWORK_CLIENT_003",
    description: "Import network device inventory from CSV file exported from network management system",
    status: "Active",
    lastRun: "2024-01-10 09:15:00",
    assetsCount: 45,
    createdAt: "2024-01-03T09:15:00Z"
  },
  {
    id: "DS004",
    dataSourceName: "Active Directory Users",
    assetType: "Endpoint",
    integrationMethod: "LDAP/Active Directory",
    updateFrequency: "Daily",
    clientId: "AD_CLIENT_004",
    description: "Extract user endpoint information from Active Directory for asset tracking",
    status: "Active",
    lastRun: "2024-01-15 08:00:00",
    assetsCount: 150,
    createdAt: "2024-01-04T11:45:00Z"
  },
  {
    id: "DS005",
    dataSourceName: "FTP Server Files",
    assetType: "Application",
    integrationMethod: "FTP",
    updateFrequency: "Weekly",
    clientId: "FTP_CLIENT_005",
    description: "Monitor application files and assets stored on FTP server",
    status: "Active",
    lastRun: "2024-01-13 14:20:00",
    assetsCount: 78,
    createdAt: "2024-01-05T16:20:00Z"
  }
];

const DataSourceList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [dataSources, setDataSources] = useState<DataSourceListItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteName, setConfirmDeleteName] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [useDummyData, setUseDummyData] = useState<boolean>(true); // For demo purposes

  const loadDataSources = async () => {
    setLoading(true);
    try {
      if (useDummyData) {
        // Use dummy data for demo
        const filteredData = dummyDataSources.filter(ds => 
          ds.dataSourceName.toLowerCase().includes(activeSearchTerm.toLowerCase()) ||
          ds.assetType.toLowerCase().includes(activeSearchTerm.toLowerCase()) ||
          ds.integrationMethod.toLowerCase().includes(activeSearchTerm.toLowerCase()) ||
          (ds.description && ds.description.toLowerCase().includes(activeSearchTerm.toLowerCase()))
        );
        
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedData = filteredData.slice(startIndex, endIndex);
        
        setDataSources(paginatedData);
        setTotalPages(Math.ceil(filteredData.length / pageSize));
        setTotalRecords(filteredData.length);
        setLoading(false);
      } else {
        // Use real API data
        const query = activeSearchTerm ? `query=${encodeURIComponent(activeSearchTerm)}&` : '';
        const result = await fetchDataApi(`data-sources?${query}page=${page}&perPage=${pageSize}`);
        setDataSources(result.data || []);
        setTotalPages(result.totalPages || 1);
        setTotalRecords(result.total || 0);
        setLoading(false);
      }
    } catch (error: any) {
      console.error('loadDataSources error:', error);
      toast.error(error.message || t('dataResource.list.toast.fetchFailed', { defaultValue: 'Failed to load data sources' }));
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDataSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, activeSearchTerm, useDummyData]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setActiveSearchTerm(searchTerm);
    setPage(1);
  };

  const openDeleteModal = (id: string, name: string) => {
    setConfirmDeleteId(id);
    setConfirmDeleteName(name);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;

    setDeletingId(confirmDeleteId);
    try {
      if (!useDummyData) {
        await deleteDataSourceApi(confirmDeleteId);
      }
      toast.success(t('dataResource.list.toast.deleteSuccess', { defaultValue: 'Data source deleted successfully' }));
      loadDataSources();
    } catch (error: any) {
      toast.error(error.message || t('dataResource.list.toast.deleteFailed', { defaultValue: 'Failed to delete data source' }));
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
      setConfirmDeleteName(null);
      setIsModalOpen(false);
    }
  };

  const handleRun = async (dataSource: DataSourceListItem) => {
    try {
      toast.info(t('dataResource.list.toast.running', { name: dataSource.dataSourceName, defaultValue: `Running ${dataSource.dataSourceName}...` }));
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Simulate success response - generate random number of new assets
      const newAssetsCount = Math.floor(Math.random() * 50) + 10; // Random number between 10-60
      const currentAssetsCount = dataSource.assetsCount || 0;
      const updatedAssetsCount = currentAssetsCount + newAssetsCount;
      
      toast.success(t('dataResource.list.toast.runSuccess', { count: newAssetsCount, name: dataSource.dataSourceName, defaultValue: `Successfully imported ${newAssetsCount} assets from ${dataSource.dataSourceName}` }));
      
      // Update the last run time and assets count in dummy data
      if (useDummyData) {
        const updatedDataSources = dataSources.map(ds => 
          ds.id === dataSource.id 
            ? { 
                ...ds, 
                lastRun: new Date().toLocaleString(),
                assetsCount: updatedAssetsCount,
                status: "Active"
              }
            : ds
        );
        setDataSources(updatedDataSources);
      } else {
        loadDataSources();
      }
    } catch (error: any) {
      toast.error(error.message || t('dataResource.list.toast.runFailed', { message: error.message, defaultValue: 'Failed to run data source' }));
    }
  };

  return (
    <div className="container-fluid" style={{ paddingTop: '100px', paddingBottom: '20px' }}>
      <Breadcrumb>
        <BreadcrumbItem>
          <a href="/">{t('common.home', { defaultValue: 'Home' })}</a>
        </BreadcrumbItem>
        <BreadcrumbItem active>
          {t('dataResource.list.titlePage', { defaultValue: 'Data Sources' })}
        </BreadcrumbItem>
      </Breadcrumb>

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h2 className="mb-0">{t('dataResource.list.titlePage', { defaultValue: 'Data Sources' })}</h2>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate('/dataresource/create')}
          >
            <MdOutlineAdd size={20} className="me-1" />
            {t('dataResource.list.create', { defaultValue: 'Create Data Source' })}
          </button>
        </div>

        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <form onSubmit={handleSearch} className="d-flex gap-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('dataResource.list.searchPlaceholder', { defaultValue: 'Search data sources...' }) as string}
                  value={searchTerm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                />
                <button type="submit" className="btn btn-outline-primary">
                  {t('common.search', { defaultValue: 'Search' })}
                </button>
                {activeSearchTerm && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setSearchTerm('');
                      setActiveSearchTerm('');
                      setPage(1);
                    }}
                  >
                    {t('common.clear', { defaultValue: 'Clear' })}
                  </button>
                )}
              </form>
            </div>
            <div className="col-md-6 text-end">
              <p className="mb-0">
                {t('dataResource.list.totalRecords', { defaultValue: 'Total records' })}: <strong>{totalRecords}</strong>
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">{t('common.loading', { defaultValue: 'Loading...' })}</span>
              </div>
            </div>
          ) : dataSources.length === 0 ? (
            <p className="text-center text-muted py-5">
              {t('dataResource.list.noData', { defaultValue: 'No data sources found' })}
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>{t('dataResource.list.headers.dataSource', { defaultValue: 'Data Source' })}</th>
                    <th>{t('dataResource.list.headers.assetType', { defaultValue: 'Asset Type' })}</th>
                    <th>{t('dataResource.list.headers.integration', { defaultValue: 'Integration' })}</th>
                    <th>{t('dataResource.list.headers.status', { defaultValue: 'Status' })}</th>
                    <th>{t('dataResource.list.headers.lastRun', { defaultValue: 'Last Run' })}</th>
                    <th>{t('dataResource.list.headers.assets', { defaultValue: 'Assets' })}</th>
                    <th>{t('dataResource.list.headers.actions', { defaultValue: 'Actions' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {dataSources.map((dataSource) => (
                    <tr key={dataSource.id}>
                      <td>
                        <div>
                          <div className="fw-bold">{dataSource.dataSourceName}</div>
                          {dataSource.description && (
                            <small className="text-dark" style={{ color: '#495057' }}>{dataSource.description}</small>
                          )}
                        </div>
                      </td>
                      <td>{dataSource.assetType}</td>
                      <td>{dataSource.integrationMethod}</td>
                      <td>
                        <span className={`badge ${dataSource.status === 'Active' ? 'bg-success' : 'bg-danger'}`}>
                          {dataSource.status || 'Inactive'}
                        </span>
                      </td>
                      <td>{dataSource.lastRun || 'N/A'}</td>
                      <td>{dataSource.assetsCount || 0}</td>
                      <td>
                        <div className="d-flex gap-2 flex-nowrap">
                          <button
                            type="button"
                            className="btn btn-sm btn-success d-flex align-items-center gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRun(dataSource);
                            }}
                            title={t('dataResource.list.actions.run', { defaultValue: 'Run Integration' }) as string}
                          >
                            <FaPlay size={12} />
                            <span>{t('dataResource.list.actions.runShort', { defaultValue: 'Run' })}</span>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dataresource/${dataSource.id}`);
                            }}
                            title={t('dataResource.list.actions.view', { defaultValue: 'View' }) as string}
                          >
                            <MdOutlineVisibility size={14} />
                            <span>{t('dataResource.list.actions.viewShort', { defaultValue: 'View' })}</span>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-warning d-flex align-items-center gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dataresource/update/${dataSource.id}`);
                            }}
                            title={t('dataResource.list.actions.edit', { defaultValue: 'Edit' }) as string}
                          >
                            <MdEdit size={14} />
                            <span>{t('dataResource.list.actions.editShort', { defaultValue: 'Edit' })}</span>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger d-flex align-items-center gap-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteModal(dataSource.id, dataSource.dataSourceName);
                            }}
                            title={t('dataResource.list.actions.delete', { defaultValue: 'Delete' }) as string}
                          >
                            <FaTrash size={12} />
                            <span>{t('dataResource.list.actions.deleteShort', { defaultValue: 'Delete' })}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                <span>
                  {t('common.page', { defaultValue: 'Page' })} {page} {t('common.of', { defaultValue: 'of' })} {totalPages}
                </span>
              </div>
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  {t('common.previous', { defaultValue: 'Previous' })}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  {t('common.next', { defaultValue: 'Next' })}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(false)}>
        <ModalHeader toggle={() => setIsModalOpen(false)}>
          {t('dataResource.list.modal.confirmTitle', { defaultValue: 'Confirm Delete' })}
        </ModalHeader>
        <ModalBody>
          <p>
            {t('dataResource.list.modal.confirmMessage', { defaultValue: 'Are you sure you want to delete' })}{' '}
            <strong>{confirmDeleteName}</strong>?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsModalOpen(false)} disabled={!!deletingId}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button color="danger" onClick={handleConfirmDelete} disabled={!!deletingId}>
            {deletingId ? t('common.deleting', { defaultValue: 'Deleting...' }) : t('common.delete', { defaultValue: 'Delete' })}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default DataSourceList;

